import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { Permissions } from '../../constants/permissions';
import { vehiclesApi, VehicleDto, CreateVehicleDto, UpdateVehicleDto } from '../../services/api';
import { usePermission } from '../../hooks/usePermission';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card } from '../../components/ui/card';

export const VehiclesPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const canView = usePermission(Permissions.Logistics.ViewVehicles);
  const canCreate = usePermission(Permissions.Logistics.CreateVehicles);
  const canEdit = usePermission(Permissions.Logistics.EditVehicles);
  const canDelete = usePermission(Permissions.Logistics.DeleteVehicles);
  
  const [vehicles, setVehicles] = useState<VehicleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<VehicleDto | null>(null);
  const [formData, setFormData] = useState<CreateVehicleDto & { isActive?: boolean }>({
    name: '',
    licensePlate: '',
    vehicleType: 1, // 1 = OwnFleet
    maxCapacityKg: undefined,
    loadAreaLengthCm: undefined,
    loadAreaWidthCm: undefined,
    loadAreaHeightCm: undefined,
  });

  useEffect(() => {
    setPageConfig({
      title: 'Vehículos',
      actions: canCreate ? [
        {
          label: 'Nuevo Vehículo',
          icon: Plus,
          onClick: () => {
            setEditingVehicle(null);
            setFormData({ 
              name: '', 
              licensePlate: '', 
              vehicleType: 1, // 1 = OwnFleet
              maxCapacityKg: undefined,
              loadAreaLengthCm: undefined,
              loadAreaWidthCm: undefined,
              loadAreaHeightCm: undefined
            });
            setShowForm(true);
          },
          variant: 'primary' as const
        }
      ] : []
    });
  }, [setPageConfig, canCreate]);

  const loadVehicles = async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await vehiclesApi.getAll({ isActive: showInactive ? undefined : true });
      setVehicles(data);
    } catch (error) {
      alert('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, [showInactive]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await vehiclesApi.update(editingVehicle.id, formData as UpdateVehicleDto);
        alert('Vehículo actualizado');
      } else {
        await vehiclesApi.create(formData);
        alert('Vehículo creado');
      }
      setShowForm(false);
      loadVehicles();
    } catch (error: any) {
      alert(error.message || 'Error al guardar vehículo');
    }
  };

  const handleEdit = (vehicle: VehicleDto) => {
    setEditingVehicle(vehicle);
    // Map string vehicle type to number
    const vehicleTypeNum = vehicle.vehicleType === 'OwnFleet' ? 1 : 2;
    setFormData({
      name: vehicle.name,
      licensePlate: vehicle.licensePlate,
      vehicleType: vehicleTypeNum,
      maxCapacityKg: vehicle.maxCapacityKg || undefined,
      loadAreaLengthCm: vehicle.loadAreaLengthCm || undefined,
      loadAreaWidthCm: vehicle.loadAreaWidthCm || undefined,
      loadAreaHeightCm: vehicle.loadAreaHeightCm || undefined,
      isActive: vehicle.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este vehículo?')) return;
    try {
      await vehiclesApi.delete(id);
      alert('Vehículo eliminado');
      loadVehicles();
    } catch (error: any) {
      alert(error.message || 'Error al eliminar');
    }
  };

  const filteredVehicles = vehicles.filter(v => 
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.licensePlate.toLowerCase().includes(search.toLowerCase())
  );

  if (!canView) return <div className="p-6">No tienes permisos para ver vehículos</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o matrícula..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="show-inactive"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="show-inactive">Mostrar inactivos</Label>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredVehicles.map((vehicle) => (
            <Card key={vehicle.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">{vehicle.name}</h3>
                  <p className="text-sm text-muted-foreground">Matrícula: {vehicle.licensePlate}</p>
                  <p className="text-sm">Tipo: {vehicle.vehicleType === 'OwnFleet' ? 'Flota propia' : 'Transportista externo'}</p>
                  {vehicle.maxCapacityKg && <p className="text-sm">Capacidad: {vehicle.maxCapacityKg} kg</p>}
                  {(vehicle.loadAreaLengthCm || vehicle.loadAreaWidthCm || vehicle.loadAreaHeightCm) && (
                    <p className="text-sm">
                      Área de carga: {vehicle.loadAreaLengthCm || '?'}×{vehicle.loadAreaWidthCm || '?'}×{vehicle.loadAreaHeightCm || '?'} cm
                    </p>
                  )}
                </div>
                <span className={`px-2 py-1 text-xs rounded ${vehicle.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {vehicle.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="flex gap-2 justify-end">
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => handleEdit(vehicle)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(vehicle.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{editingVehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}</DialogTitle>
              <DialogDescription>
                Complete los datos del vehículo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="licensePlate">Matrícula *</Label>
                <Input
                  id="licensePlate"
                  value={formData.licensePlate}
                  onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="vehicleType">Tipo *</Label>
                <Select
                  value={formData.vehicleType.toString()}
                  onValueChange={(value) => setFormData({ ...formData, vehicleType: Number(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Flota propia</SelectItem>
                    <SelectItem value="2">Transportista externo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="maxCapacityKg">Capacidad máxima (kg)</Label>
                <Input
                  id="maxCapacityKg"
                  type="number"
                  value={formData.maxCapacityKg || ''}
                  onChange={(e) => setFormData({ ...formData, maxCapacityKg: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="loadAreaLengthCm">Largo (cm)</Label>
                  <Input
                    id="loadAreaLengthCm"
                    type="number"
                    placeholder="Largo"
                    value={formData.loadAreaLengthCm || ''}
                    onChange={(e) => setFormData({ ...formData, loadAreaLengthCm: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
                <div>
                  <Label htmlFor="loadAreaWidthCm">Ancho (cm)</Label>
                  <Input
                    id="loadAreaWidthCm"
                    type="number"
                    placeholder="Ancho"
                    value={formData.loadAreaWidthCm || ''}
                    onChange={(e) => setFormData({ ...formData, loadAreaWidthCm: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
                <div>
                  <Label htmlFor="loadAreaHeightCm">Alto (cm)</Label>
                  <Input
                    id="loadAreaHeightCm"
                    type="number"
                    placeholder="Alto"
                    value={formData.loadAreaHeightCm || ''}
                    onChange={(e) => setFormData({ ...formData, loadAreaHeightCm: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
              </div>
              {editingVehicle && (
                <div className="flex items-center gap-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="isActive">Activo</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
